import './index.scss';

import logo from '../../assets/images/icons/logo.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { auth } from '../../firebase/firebaseConfig';
import countryList from '../../json/countryCode.json';
import ReactCodeField from 'react-code-input';
import { useNavigate } from 'react-router-dom';
import { addDays, addHours } from 'date-fns';

const SESSION_LENGTH_HOURS = 24;
const TRUSTED_DEVICE_DAYS = 30;
const TRUSTED_DEVICE_KEY = '2FA_TRUSTED_DEVICE@ZonaAdmin';

export default function TwoFactorAuth() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [validation, setValidation] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [secondsToSendCodeAgain, setSecondsToSendCodeAgain] = useState(0);
  const [intervalId, setIntervalId] = useState();

  useEffect(() => {
    if (intervalId && secondsToSendCodeAgain === 0) {
      clearInterval(intervalId);
      setIntervalId(undefined);
    }
  }, [intervalId, secondsToSendCodeAgain]);

  useEffect(() => {
    async function loadUser() {
      const userCredentials = auth.currentUser;

      const { data } = await api.get(`/users/${userCredentials.uid}`);

      setUser(data.user);
    }

    loadUser();
  }, []);

  const implicitPhoneNumber = useMemo(() => {
    if (!user?.phone) return '';

    const phoneCountryCode = countryList.find(country => user.phone.includes(country.dial_code));
    const phoneWithoutDialCode = user.phone.replace(phoneCountryCode.dial_code, '');

    const init = phoneWithoutDialCode.length - 4;
    const last4Digits = phoneWithoutDialCode.substring(init);

    const implicitPhoneNumber = last4Digits.padStart(phoneWithoutDialCode.length, '*');

    return phoneCountryCode.dial_code + implicitPhoneNumber;
  }, [user]);

  const handleCodeVerification = useCallback(async event => {
    event.preventDefault();

    try {
      setLoading(true);

      const { data } = await api.post(`/users/verify-2fa-code`, {
        code,
        remember: rememberDevice,
      });

      if (!data.verified) {
        setValidation('Could not validate your code...');
        return;
      }

      const oldTwoFactorAuth = localStorage.getItem('2FA@ZonaAdmin');
      const newTwoFactor = JSON.parse(oldTwoFactorAuth);

      newTwoFactor.verified = true;
      // Was left at the ~15-minute "code must be entered by" deadline set
      // when the code was sent - meaning a verified session got force-
      // logged-out 15 minutes after login. Reset to the real session length
      // now that verification actually succeeded.
      newTwoFactor.expires = addHours(Date.now(), SESSION_LENGTH_HOURS).getTime();

      localStorage.setItem('2FA@ZonaAdmin', JSON.stringify(newTwoFactor));

      if (rememberDevice && data.device_token && auth.currentUser) {
        localStorage.setItem(TRUSTED_DEVICE_KEY, JSON.stringify({
          uid: auth.currentUser.uid,
          expires: addDays(Date.now(), TRUSTED_DEVICE_DAYS).getTime(),
          token: data.device_token,
        }));
      }

      navigate('/');
    } catch (err) {
      if (err?.response?.data) setValidation(err?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [code, user, navigate, rememberDevice]);

  // Sent here by the server asking for the code (not from sign-in): text one now.
  useEffect(() => {
    try {
      const pending = JSON.parse(localStorage.getItem('2FA@ZonaAdmin') || 'null');
      if (pending && pending.sendCode) {
        localStorage.setItem('2FA@ZonaAdmin', JSON.stringify({ ...pending, sendCode: false }));
        api.post('/users/send-2fa-code').catch(() => {});
      }
    } catch (e) {
      // Malformed value: the Resend button still works.
    }
  }, []);

  const onCodeChange = useCallback(text => {
    setCode(text);
  }, []);

  const handleResendCode = useCallback(async () => {
    setSecondsToSendCodeAgain(60);

    await api.post('/users/send-2fa-code');

    const interval = setInterval(() => {
      setSecondsToSendCodeAgain(oldState => oldState - 1);
    }, 1000);

    setIntervalId(interval);
  }, [user]);

  return (
    <div id="two-f-a">
      <form onSubmit={handleCodeVerification}>
        <img src={logo} alt="Logo" />

        <p>
          We sent a verification code to the phone number attached to your account <strong>{implicitPhoneNumber}</strong>
        </p>

        <ReactCodeField
          inputMode="numeric"
          fields={4}
          inputStyle={{
            backgroundColor: '#FFFFFF1A',
            borderRadius: 8,
            width: 60,
            height: 54,
            border: 0,
            fontSize: 32,
            color: '#fff',
            textAlign: 'center',
          }}
          onChange={onCodeChange}
        />

        {!!validation && <span>{validation}</span>}

        <div>
          {secondsToSendCodeAgain ? (
            <span>Code sent! Send again? ({secondsToSendCodeAgain})</span>
          ) : (
            <>
              <span>Didn't get a code?</span>

              <button disabled={!user?.phone} onClick={handleResendCode} type="button">
                Resend code
              </button>
            </>
          )}

        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={event => setRememberDevice(event.target.checked)}
          />
          Remember this device for 30 days
        </label>

        <button disabled={code.length !== 4 || loading} type="submit">
          {loading ? 'Verifying...' : 'Confirm'}
        </button>
      </form>
    </div>
  );
}